import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Repeat, Calendar, MapPin, BarChart3, X, Trash2, ChevronDown, MessageSquare, Users } from 'lucide-react';
import { recurringAPI } from '../lib/api';
import { isAdmin, ROLE_LABELS } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const RECURRENCE_LABELS = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };

function OccurrenceRow({ occ, canEdit, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(occ.occurrence_date?.slice(0, 10) || '');
  const [time, setTime] = useState(occ.time || '');
  const isPast = occ.occurrence_date && new Date(occ.occurrence_date) < new Date();

  const save = async () => {
    try {
      const r = await recurringAPI.updateOccurrence(occ.event_id, { date, time });
      onUpdated(r.data);
      setEditing(false);
      toast.success('Occurrence updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const toggleCancel = async () => {
    try {
      const r = await recurringAPI.updateOccurrence(occ.event_id, { is_cancelled: !occ.is_cancelled });
      onUpdated(r.data);
      toast.success(occ.is_cancelled ? 'Occurrence restored' : 'Occurrence cancelled');
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-800/60 text-sm" data-testid={`occurrence-${occ.event_id}`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isPast ? 'bg-gray-600' : 'bg-red-500'}`} />
      {editing ? (
        <div className="flex-1 flex items-center gap-2">
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-[#111] border border-gray-700 text-white px-2 py-1.5 rounded text-xs" />
          <input type="time" value={time} onChange={e => setTime(e.target.value)} className="bg-[#111] border border-gray-700 text-white px-2 py-1.5 rounded text-xs" />
          <button onClick={save} className="text-xs text-red-400 font-bold">Save</button>
          <button onClick={() => setEditing(false)} className="text-xs text-gray-500">Cancel</button>
        </div>
      ) : (
        <>
          <div className="flex-1">
            <span className="text-white">{occ.occurrence_date?.slice(0, 10)}</span>
            {occ.is_cancelled && <span className="ml-2 text-red-400 text-xs font-bold uppercase">Cancelled</span>}
          </div>
          <span className="text-gray-500 text-xs">{occ.tickets_sold || 0} tickets · €{(occ.revenue || 0).toFixed(2)}</span>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(true)} className="text-xs text-gray-400 hover:text-white">Edit</button>
              <button onClick={toggleCancel} className={`text-xs ${occ.is_cancelled ? 'text-green-400' : 'text-red-400'}`}>
                {occ.is_cancelled ? 'Restore' : 'Cancel'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SeriesCard({ ser, canManage, canEditOccurrences, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [occurrences, setOccurrences] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const title = ser.base_event?.title || 'Untitled Series';
  const typeLabel = RECURRENCE_LABELS[ser.recurrence_type] || ser.recurrence_type;

  const toggle = async () => {
    setExpanded(v => !v);
    if (!loaded) {
      try {
        const r = await recurringAPI.occurrences(ser.base_event_id);
        setOccurrences(r.data.occurrences || r.data || []);
        setLoaded(true);
      } catch { toast.error('Failed to load occurrences'); }
    }
  };

  const openAnalytics = async () => {
    setShowAnalytics(true);
    if (!analytics) {
      try {
        const r = await recurringAPI.seriesAnalytics(ser.base_event_id);
        setAnalytics(r.data);
      } catch { toast.error('Failed to load analytics'); }
    }
  };

  const deleteSeries = async () => {
    if (!window.confirm('Delete this entire series and all its occurrences?')) return;
    try {
      await recurringAPI.deleteSeries(ser.base_event_id);
      onDeleted(ser.base_event_id);
      toast.success('Series deleted');
    } catch { toast.error('Failed to delete series'); }
  };

  return (
    <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-5" data-testid={`series-${ser.base_event_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <span className="inline-flex items-center gap-1 text-xs font-bold uppercase px-2 py-0.5 rounded bg-red-600/20 text-red-400 border border-red-600/30 mb-2">
            <Repeat size={11} /> {typeLabel}
          </span>
          <h3 className="text-white font-black uppercase text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>{title}</h3>
          <div className="flex items-center gap-3 text-gray-500 text-xs mt-1">
            <span className="flex items-center gap-1"><Calendar size={11} /> {ser.total_occurrences} occurrences</span>
            {ser.base_event?.city && <span className="flex items-center gap-1"><MapPin size={11} /> {ser.base_event.city}</span>}
          </div>
        </div>
        {canManage && (
          <button onClick={deleteSeries} className="text-gray-600 hover:text-red-400" data-testid={`delete-series-${ser.base_event_id}`}>
            <Trash2 size={15} />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button onClick={toggle} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-3 py-1.5 border border-gray-700 rounded transition-colors">
          <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} /> Occurrences
        </button>
        {ser.base_event?.event_id && (
          <Link to={`/events/${ser.base_event.event_id}`} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-3 py-1.5 border border-gray-700 rounded transition-colors">
            <MessageSquare size={12} /> Moderate Chat
          </Link>
        )}
        {(ser.my_permissions || []).includes('view_sales') && (
          <button onClick={openAnalytics} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-3 py-1.5 border border-gray-700 rounded transition-colors" data-testid={`analytics-${ser.base_event_id}`}>
            <BarChart3 size={12} /> Analytics
          </button>
        )}
        <Link to={`/team/series/${ser.base_event_id}`} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-3 py-1.5 border border-gray-700 rounded transition-colors">
          <Users size={12} /> Team & lineup
        </Link>
        {(ser.my_roles || []).length > 0 && (
          <span className="text-[10px] font-bold uppercase text-yellow-400 ml-auto">
            {ser.my_roles.map(r => ROLE_LABELS[r] || r).join(' · ')}
          </span>
        )}
      </div>

      {expanded && (
        <div className="mt-4 pt-3 border-t border-gray-800">
          {occurrences.length === 0 ? (
            <p className="text-gray-600 text-xs">No occurrences found.</p>
          ) : occurrences.map(occ => (
            <OccurrenceRow
              key={occ.event_id}
              occ={occ}
              canEdit={canEditOccurrences}
              onUpdated={upd => setOccurrences(prev => prev.map(o => o.event_id === upd.event_id ? upd : o))}
            />
          ))}
        </div>
      )}

      {showAnalytics && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAnalytics(false)}>
          <div className="bg-[#0A0A0A] border border-gray-700 rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-800">
              <h3 className="text-white font-bold text-sm truncate">{title}</h3>
              <button onClick={() => setShowAnalytics(false)} className="text-gray-500 hover:text-white"><X size={18} /></button>
            </div>
            {analytics ? (
              <div className="p-5">
                <div className="flex bg-[#111] border border-gray-800 rounded-lg p-4 mb-4">
                  <div className="flex-1 text-center">
                    <p className="text-white font-black text-2xl">{analytics.totals?.tickets_sold ?? 0}</p>
                    <p className="text-gray-500 text-xs">Total Tickets</p>
                  </div>
                  <div className="w-px bg-gray-800" />
                  <div className="flex-1 text-center">
                    <p className="text-green-400 font-black text-2xl">€{(analytics.totals?.revenue ?? 0).toFixed(2)}</p>
                    <p className="text-gray-500 text-xs">Total Revenue</p>
                  </div>
                </div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Per Occurrence</p>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {(analytics.occurrences || []).map(o => (
                    <div key={o.event_id} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-800/60">
                      <span className="text-gray-300">{o.occurrence_date?.slice(0, 10)}</span>
                      <span className="text-gray-500">{o.tickets_sold} tickets · €{(o.revenue || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 text-sm">Loading...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RecurringSeriesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Rights are per series now (owner, admin, or a team assignment such as series
  // manager). A DJ booked on a series can view it but not edit dates unless also
  // given a manager role. DJs have their own dashboard at /dj.
  const canManage = (ser) => !!ser.is_owner || isAdmin(user);
  const canEditOccurrences = (ser) => (ser.my_permissions || []).includes('edit_event');

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth?redirect=/recurring-series');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    recurringAPI.mySeries()
      .then(r => setSeries(Array.isArray(r.data) ? r.data : r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="recurring-series-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Recurring Series
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Series you own, plus series you're on the team for (manager, DJ, promoter…).
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2].map(i => <div key={i} className="skeleton h-40 rounded-xl" />)}</div>
        ) : series.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-gray-800 rounded-xl">
            <Repeat className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              No recurring series yet. Convert an event to recurring from your dashboard.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {series.map(ser => (
              <SeriesCard
                key={ser.pattern_id || ser.base_event_id}
                ser={ser}
                canManage={canManage(ser)}
                canEditOccurrences={canEditOccurrences(ser)}
                onDeleted={id => setSeries(prev => prev.filter(s => s.base_event_id !== id))}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
